import React, { useEffect, useState } from 'react';
import { Loader, Home, Folder, Trash2, ChevronRight } from 'lucide-react';
import { fetchDirectories } from '../../services/assetManagerService';
import ConfirmModal from '../common/ConfirmModal';
import { ScrollArea } from "@/components/ui/scroll-area"
import { useLocation } from 'react-router-dom';

interface SidebarProps {
  onNavigate: (folder: string) => void;
  onDeleteFolder?: (folder: string) => void;
  currentFolder: string;
  directoryUpdateTrigger: number;
  isPicker?: boolean; 
}

interface Directory {
  name: string;
  path: string;
  type: string;
  children: Directory[];
}

export const Sidebar: React.FC<SidebarProps> = ({
  onNavigate,
  onDeleteFolder,
  currentFolder,
  directoryUpdateTrigger,
  isPicker = false, 
}) => {
  const { pathname } = useLocation();
  const [directories, setDirectories] = useState<Directory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<{ name: string; path: string } | null>(null);

  useEffect(() => {
    const loadDirectories = async () => {
      setIsLoading(true);
      try {
        const dirs = await fetchDirectories('/');
        setDirectories(dirs);
      } catch (error) {
        console.error('Error fetching directories:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadDirectories();
  }, [directoryUpdateTrigger]);

  const handleDeleteClick = (event: React.MouseEvent, dir: Directory) => {
    event.stopPropagation();
    setFolderToDelete({ name: dir.name, path: dir.path });
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (folderToDelete) {
      onDeleteFolder?.(folderToDelete.path);
      setIsDeleteModalOpen(false);
      setFolderToDelete(null);
    }
  };
  
  const renderDirectories = (dirs: Directory[]) => {
    return dirs.map((dir) => {
      const isCurrentFolder = currentFolder === dir.path;
      return (
        <li key={dir.path}>
          <div
            className={`flex items-center justify-between w-full py-2 px-3 rounded-md hover:bg-gray-100 transition duration-200 ease-in-out ${
              isCurrentFolder ? 'bg-blue-100' : ''
            }`}
          >
            <button
              className="flex items-center text-left flex-grow text-sm"
              onClick={() => onNavigate(dir.path)}
            >
              <ChevronRight size={14} className="mr-1" />
              <Folder
                className={`mr-3 ${isCurrentFolder ? 'text-primary-800' : ''}`}
                size={14}
              />
              <span
                className={
                  isCurrentFolder ? 'font-semibold text-primary-800' : ''
                }
              >
                {dir.name}
              </span>
            </button>
            {!isPicker && onDeleteFolder && ( 
              <button
                onClick={(e) => handleDeleteClick(e, dir)}
                className="p-1 rounded-full hover:bg-gray-200 transition duration-200"
              >
                <Trash2 size={16} className="text-gray-500 hover:text-red-500" />
              </button>
            )}
          </div>
          {dir.children && dir.children.length > 0 && (
            <ul className="pl-6">{renderDirectories(dir.children)}</ul>
          )}
        </li>
      );
    });
  };
  return (
    <div className="w-full sm:w-63 p-3 sm:p-4 z-10">
      <ScrollArea className={pathname  === "/asset-manager" ? 'h-full' : 'h-[85vh]'}>
        <ul className="space-y-2">
          <li>
            <button
              className="flex items-center w-full text-left py-2 px-3 rounded-md hover:bg-gray-100 transition duration-200 ease-in-out text-sm"
              onClick={() => onNavigate('/')}
            >
              <Home className="mr-3" size={14} />
              <span>Home</span>
            </button>
          </li>
          {isLoading ? (
            <li className="flex items-center justify-center py-4">
              <Loader className="animate-spin" size={24} />
            </li>
          ) : (
            renderDirectories(directories)
          )}
        </ul>
      </ScrollArea>
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Confirm Deletion"
        message={`Are you sure you want to delete the folder "${folderToDelete?.name || ''}"`}
      />
    </div>
  );
};
